import { RouterProvider } from '@tanstack/react-router';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import './styles/index.css';
import { installSettingsSync, pullSyncedSettings } from './account/settingsSync.ts';
import { useAccount } from './account/store.ts';
import { api } from './api/instance.ts';
import { offerBackup } from './app/backupOffer.ts';
import { setAppRouter } from './app/nav.ts';
import { engineRef } from './editor/canvas/engine.ts';
import { installNotificationClicks } from './renders/notify.ts';
import { router } from './router.tsx';
import { services } from './services/index.ts';
import { installAutosave } from './state/autosave.ts';
import { useDocument } from './state/documentStore.ts';
import { startLibrary, useLibrary } from './state/library.ts';
import { hydrateSettings, useSettings } from './state/settingsStore.ts';
import { useUi } from './state/uiStore.ts';
import { initStorage } from './storage/index.ts';
import { loadSettings } from './storage/settings.ts';
import { useSync } from './sync/service.ts';
import { captureError, initSentry } from './telemetry/sentry.ts';

async function boot(): Promise<void> {
  await initStorage();
  hydrateSettings(await loadSettings());
  startLibrary();
  installAutosave();
  setAppRouter(router);
  installNotificationClicks();
  installSettingsSync();
  services.account.onSignedIn((first) => {
    void pullSyncedSettings();
    services.sync.start();
    void services.render.resume();
    if (first) void offerBackup();
  });
  services.account.onSignedOut(() => services.sync.stop());
  await services.account.init();
  // Test / debugging hook (read-only use in Playwright).
  Object.assign(window, {
    __betterLights: {
      engine: engineRef,
      document: useDocument,
      settings: useSettings,
      ui: useUi,
      library: useLibrary,
      account: useAccount,
      sync: useSync,
      services,
      api,
    },
  });
  createRoot(document.getElementById('root')!, {
    onUncaughtError: (error, info) => captureError(error, { componentStack: info.componentStack }),
    onCaughtError: (error, info) => captureError(error, { componentStack: info.componentStack }),
  }).render(
    <StrictMode>
      <RouterProvider router={router} />
    </StrictMode>,
  );
  // Crash reporting loads after the first render (lazy chunk; production + VITE_SENTRY_DSN only).
  setTimeout(() => void initSentry(), 0);
  if (import.meta.env.PROD && 'serviceWorker' in navigator) {
    const { registerSW } = await import('virtual:pwa-register');
    registerSW({ immediate: true });
  }
}

void boot();
